import React from 'react';
import { Link } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-amber-400 to-amber-400 bg-clip-text text-transparent">
            Privacy Policy
          </h1>
          <p className="text-gray-400">
            Effective as of January 26, 2026
          </p>
        </div>

        {/* Table of Contents */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-12">
          <h2 className="text-xl font-semibold mb-4 text-amber-400">Table of Contents</h2>
          <div className="grid md:grid-cols-2 gap-2 text-sm">
            <a href="#introduction" className="text-gray-300 hover:text-amber-400 transition-colors">1. Introduction</a>
            <a href="#information-collection" className="text-gray-300 hover:text-amber-400 transition-colors">2. Information We Collect</a>
            <a href="#how-we-use" className="text-gray-300 hover:text-amber-400 transition-colors">3. How We Use Information</a>
            <a href="#sharing" className="text-gray-300 hover:text-amber-400 transition-colors">4. Information Sharing</a>
            <a href="#cookies" className="text-gray-300 hover:text-amber-400 transition-colors">5. Cookies and Tracking</a>
            <a href="#security" className="text-gray-300 hover:text-amber-400 transition-colors">6. Data Security</a>
            <a href="#your-rights" className="text-gray-300 hover:text-amber-400 transition-colors">7. Your Rights</a>
            <a href="#state-specific" className="text-gray-300 hover:text-amber-400 transition-colors">8. State-Specific Rights</a>
            <a href="#children" className="text-gray-300 hover:text-amber-400 transition-colors">9. Children's Privacy</a>
            <a href="#international" className="text-gray-300 hover:text-amber-400 transition-colors">10. International Users</a>
            <a href="#changes" className="text-gray-300 hover:text-amber-400 transition-colors">11. Policy Changes</a>
            <a href="#contact" className="text-gray-300 hover:text-amber-400 transition-colors">12. Contact Us</a>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          
          {/* Introduction */}
          <section id="introduction" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">1. Introduction</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                This privacy policy ("Privacy Policy") provides a detailed description of how Dotstudioz, INC and its affiliates including, but not limited to dotstudioPRO, Sports.TV ("us," "we," or "our") collect, use, and share information about you as well as your rights and choices regarding such information.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                This Privacy Policy applies to any online location, mobile applications, interactive TV applications, desktop applications, digital media players, and other digital services that link to this Privacy Policy (the "Service") including the following:
              </p>
              <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                <li>sports.tv</li>
                <li>dotstudiopro.com</li>
                <li>corp.dotstudiopro.com</li>
                <li>dotstudioz.com</li>
                <li>chillfree.net</li>
                {/* TODO_SPORTSTV: confirm correct Sports.TV legal domain (chillfree.net left as-is above) */}
                <li>other Dotstudioz owned websites</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                By using our Service, you consent to the applicable terms of use for the respective Service and agree to the collection and use of information from or about you as explained in this Privacy Policy. If you do not agree and consent to the collection and use of information from or about you in accordance with this Privacy Policy, then you may not use and/or must discontinue use of the Service.
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section id="information-collection" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">2. Information We Collect</h2>
            <div className="space-y-6">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-amber-400">Personal Information</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We collect personally identifiable information that you voluntarily provide to us, including:
                </p>
                <ul className="text-gray-300 list-disc list-inside space-y-1">
                  <li>Email address</li>
                  <li>Full name</li>
                  <li>Mailing address</li>
                  <li>Phone numbers (work, cell, and home)</li>
                  <li>Account preferences and settings</li>
                  <li>Communication preferences</li>
                </ul>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-amber-400">Usage Information</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We automatically collect information about how you use our Service, including:
                </p>
                <ul className="text-gray-300 list-disc list-inside space-y-1">
                  <li>Viewing history and preferences</li>
                  <li>Device information and identifiers</li>
                  <li>IP address and location data</li>
                  <li>Browser type and version</li>
                  <li>Operating system information</li>
                  <li>Referral sources and website navigation patterns</li>
                </ul>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-amber-400">Transactional and Billing Information</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  If you rent or purchase content through Transactional Video On Demand (TVOD) or Electronic Sell Through (EST), we may collect and process limited transaction-related information including:
                </p>
                <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                  <li>Purchase type (rental or digital purchase)</li>
                  <li>Transaction date and time</li>
                  <li>Title rented or purchased</li>
                  <li>Platform of purchase</li>
                  <li>Transaction confirmation identifiers or receipt validation data</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Payments made through third-party platforms, including but not limited to Apple App Store, Google Play Store, Roku, Amazon Fire TV, Vizio, Samsung, LG, or other connected TV or mobile marketplaces, are processed directly by those platforms.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  We do not collect or store full payment card information. We receive only the information necessary to validate and fulfill your transaction.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section id="how-we-use" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">3. How We Use Information</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                We use the information we collect for various purposes, including:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-amber-400 mb-2">Service Provision</h4>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1">
                    <li>Providing and maintaining our Service</li>
                    <li>Processing your requests and transactions</li>
                    <li>Personalizing your experience</li>
                    <li>Providing customer support</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-amber-400 mb-2">Communication</h4>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1">
                    <li>Sending service-related notifications</li>
                    <li>Responding to your inquiries</li>
                    <li>Sending promotional materials (with consent)</li>
                    <li>Conducting surveys and research</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-amber-400 mb-2">Improvement</h4>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1">
                    <li>Analyzing usage patterns</li>
                    <li>Improving our Service and content</li>
                    <li>Developing new features</li>
                    <li>Troubleshooting technical issues</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-amber-400 mb-2">Legal Compliance</h4>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1">
                    <li>Complying with legal obligations</li>
                    <li>Protecting our rights and property</li>
                    <li>Ensuring user safety and security</li>
                    <li>Preventing fraud and abuse</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section id="sharing" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">4. Information Sharing</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                We may share your information in the following circumstances:
              </p>
              <div className="space-y-4">
                <div className="border-l-4 border-amber-500 pl-4">
                  <h4 className="font-semibold text-amber-400 mb-2">With Your Consent</h4>
                  <p className="text-gray-300 text-sm">We may share your information with third parties when you have given us explicit consent to do so.</p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-semibold text-yellow-400 mb-2">Service Providers</h4>
                  <p className="text-gray-300 text-sm">We may share information with trusted service providers who assist us in operating our Service, subject to confidentiality agreements.</p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-red-400 mb-2">Legal Requirements</h4>
                  <p className="text-gray-300 text-sm">We may disclose information when required by law, court order, or to protect our rights, property, or safety.</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-blue-400 mb-2">Business Transfers</h4>
                  <p className="text-gray-300 text-sm">In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.</p>
                </div>
                <div className="border-l-4 border-amber-500 pl-4">
                  <h4 className="font-semibold text-amber-400 mb-2">Platform Payment Providers</h4>
                  <p className="text-gray-300 text-sm mb-2">When you make a rental or purchase through a third-party platform or connected TV device, payment processing is handled by that platform's billing provider. This may include, but is not limited to:</p>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1 mb-2">
                    <li>Apple App Store</li>
                    <li>Google Play Store</li>
                    <li>Roku Pay</li>
                    <li>Amazon Fire TV In-App Purchasing</li>
                    <li>Samsung Checkout</li>
                    <li>LG Billing Services</li>
                    <li>Vizio platform billing systems</li>
                  </ul>
                  <p className="text-gray-300 text-sm">Each platform operates under its own privacy policy and payment terms. We recommend reviewing the applicable platform's policies for additional information.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Cookies and Tracking */}
          <section id="cookies" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">5. Cookies and Tracking Technologies</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our Service. Cookies are small data files stored on your device that help us:
              </p>
              <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                <li>Remember your preferences and settings</li>
                <li>Provide personalized content recommendations</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Improve our Service performance and functionality</li>
                <li>Deliver relevant advertisements</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                You can control cookie settings through your browser preferences. However, disabling cookies may limit some functionality of our Service. Third-party advertisers may also use cookies, and we recommend reviewing their privacy policies for more information.
              </p>
            </div>
          </section>

          {/* Data Security */}
          <section id="security" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">6. Data Security</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                We implement reasonable administrative, technical, personnel and physical measures to safeguard information in our possession against loss, theft and unauthorized use, disclosure or modification. Our security measures include:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-amber-400 mb-2">Technical Safeguards</h4>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Secure server infrastructure</li>
                    <li>Regular security updates and patches</li>
                    <li>Firewall and intrusion detection systems</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-amber-400 mb-2">Administrative Controls</h4>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1">
                    <li>Access controls and user authentication</li>
                    <li>Employee training on data protection</li>
                    <li>Regular security audits and assessments</li>
                    <li>Incident response procedures</li>
                  </ul>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed mt-4">
                While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your data using industry-standard practices.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section id="your-rights" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">7. Your Rights and Choices</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                You have certain rights regarding your personal information:
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-400">Access and Portability</h4>
                    <p className="text-gray-300 text-sm">Request access to your personal information and receive a copy in a portable format.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-400">Correction</h4>
                    <p className="text-gray-300 text-sm">Request correction of inaccurate or incomplete personal information.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-400">Deletion</h4>
                    <p className="text-gray-300 text-sm">Request deletion of your personal information, subject to certain exceptions.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-400">Opt-Out</h4>
                    <p className="text-gray-300 text-sm">Opt out of marketing communications and certain data processing activities.</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                <p className="text-gray-300 text-sm">
                  To exercise your rights, please contact us at{' '}
                  <a href="mailto:datarequest@sports.tv" className="text-amber-400 hover:text-amber-300 underline">
                    datarequest@sports.tv
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* State-Specific Rights */}
          <section id="state-specific" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">8. State-Specific Privacy Rights</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                If you are a resident of California, Colorado, Connecticut, Utah, or Virginia, you may have additional privacy rights under state law:
              </p>
              <div className="space-y-4">
                <div className="border border-blue-500/30 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-400 mb-2">California Residents (CCPA/CPRA)</h4>
                  <p className="text-gray-300 text-sm mb-2">
                    California residents have specific rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA).
                  </p>
                  <div className="flex space-x-4">
                    <Link to="/do-not-sell" className="text-amber-400 hover:text-amber-300 underline text-sm">
                      Do Not Sell My Data
                    </Link>
                  </div>
                </div>
                <div className="border border-purple-500/30 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-400 mb-2">Other State Residents</h4>
                  <p className="text-gray-300 text-sm">
                    Residents of Colorado, Connecticut, Utah, and Virginia may have similar rights under their respective state privacy laws. Contact us for more information about your specific rights.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Children's Privacy */}
          <section id="children" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">9. Children's Privacy</h2>
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
              <p className="text-gray-300 leading-relaxed mb-4">
                Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
              </p>
              <p className="text-gray-300 leading-relaxed">
                If we become aware that we have collected personal information from a child under 13 without verification of parental consent, we will take steps to remove that information from our servers.
              </p>
            </div>
          </section>

          {/* International Users */}
          <section id="international" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">10. International Users</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                Our Service is intended for users in the United States. If you are located outside of the United States and are accessing our Service, your personal information will be processed and stored in the United States.
              </p>
              <p className="text-gray-300 leading-relaxed">
                The laws in the United States may not be as protective of personal information as laws in your resident country. If you do not agree with the processing of your personal information in the United States, please discontinue your use of the Service.
              </p>
            </div>
          </section>

          {/* Policy Changes */}
          <section id="changes" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">11. Changes to This Privacy Policy</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make changes, we will:
              </p>
              <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                <li>Update the "Effective Date" at the top of this policy</li>
                <li>Notify you of material changes through our Service or by email</li>
                <li>Provide you with an opportunity to review the updated policy</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Your continued use of our Service after any changes indicates your acceptance of the updated Privacy Policy.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section id="contact" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-amber-400">12. Contact Us</h2>
            <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-6">
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-gray-300">
                  <strong>General Support and Transaction Assistance:</strong>{' '}
                  <a href="mailto:support@sports.tv" className="text-amber-400 hover:text-amber-300 underline">
                    support@sports.tv
                  </a>
                </p>
                <p className="text-gray-300">
                  <strong>Privacy and Data Requests:</strong>{' '}
                  <a href="mailto:datarequest@sports.tv" className="text-amber-400 hover:text-amber-300 underline">
                    datarequest@sports.tv
                  </a>
                </p>
                <p className="text-gray-300">
                  <strong>Company:</strong> Dotstudioz INC
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-amber-500/30">
                <p className="text-gray-400 text-sm">
                  We will respond to your inquiry within 30 business days of receipt.
                </p>
              </div>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;

